/**
 * AdminUserDetail — 10-tab user detail page for admin panel
 * Tabs: Perfil, Assinatura, Limites LLM, Carteira, Ambientes, Atividade, Seguranca, Indicacoes, Analiticos, Zona de Perigo
 */
import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { route } from 'preact-router';
import { T } from '../styles/tokens';
import { Button, Input, Select, ListSkeleton, SkeletonBar, Textarea } from '../components/ui';

// ── Types ──
interface UserDetail {
  id: string; email: string; name: string | null; role: string; status: string;
  phone: string | null; company: string | null; timezone: string | null;
  notes: string | null; avatar_url: string | null; language: string | null;
  country: string | null; city: string | null; address: string | null;
  postal_code: string | null; birth_date: string | null;
  billing_email: string | null; billing_name: string | null;
  billing_country: string | null; billing_document: string | null;
  signup_ip: string | null; signup_country: string | null;
  is_premium: number; premium_since: string | null; premium_expires: string | null;
  premium_plan: string | null; stripe_customer_id: string | null; stripe_subscription_id: string | null;
  token_limit_monthly: number; tokens_used_monthly: number;
  budget_limit_monthly: number; budget_used_monthly: number;
  budget_limit_daily: number; budget_used_daily: number;
  rpm_limit: number; max_machines: number; default_model: string | null;
  allowed_models: string | null; blocked_models: string | null;
  is_suspended: number; suspended_at: string | null; suspended_reason: string | null;
  two_factor_enabled: number; last_login_at: string | null;
  last_login_ip: string | null; failed_login_attempts: number;
  login_count: number; last_activity_at: string | null;
  referral_code: string | null; referred_by: string | null;
  created_at: string; updated_at: string | null;
  [key: string]: any;
}

interface Subscription {
  id: string;
  customer_id: string;
  plan: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  workspaces_limit: number | null;
  workspaces_used: number | null;
  stripe_subscription_id: string | null;
  billing_cycle: string | null;
  created_at: string;
  updated_at: string | null;
  [key: string]: any;
}

// ── Shared styles ──
const S = {
  page: { minHeight: '100vh', background: '#0A0E1A', color: T.text, padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' } as const,
  glass: { background: T.bgCard, backdropFilter: 'blur(16px)', border: `1px solid ${T.border}`, borderRadius: 16 } as const,
  input: { background: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, padding: '8px 12px', color: T.text, fontSize: 13, outline: 'none', width: '100%' } as const,
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 } as const,
  btnPrimary: { background: T.primary, color: T.text } as const,
  btnDanger: { background: T.danger, color: T.text } as const,
  label: { fontSize: 11, fontWeight: 600, color: T.textSecondary, marginBottom: 4, display: 'block' } as const,
  th: { padding: '8px 12px', textAlign: 'left' as const, fontSize: 11, fontWeight: 600, color: T.textSecondary, borderBottom: `1px solid ${T.border}` },
  td: { padding: '8px 12px', fontSize: 13, borderBottom: `1px solid ${T.bgCard}` },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: T.purple, marginBottom: 16, marginTop: 24, letterSpacing: '0.02em' } as const,
};

const PLAN_COLORS: Record<string, string> = { free: T.textMuted, pro: T.primary, business: T.purple, enterprise: T.warning };
const STATUS_COLORS: Record<string, string> = { active: T.success, canceled: T.danger, past_due: T.warning, running: T.success, stopped: T.textMuted, provisioning: T.primary, error: T.danger };

function Badge({ text, color }: { text: string; color: string }) {
  return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}30` }}>{text}</span>;
}

function Skeleton({ width = '100%', height = 16 }: { width?: string | number; height?: number }) {
  return <div style={{ width, height, borderRadius: 6, background: T.border, animation: 'pulse 1.5s infinite' }} />;
}

function ProgressBar({ value, max, color = T.primary }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: T.border, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? T.danger : color, borderRadius: 3, transition: 'width 0.3s' }} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return <div style={{ marginBottom: 14 }}><label style={S.label}>{label}</label>{children}</div>;
}

function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.label}>{label}</label>
      <div style={{ ...S.input, background: T.bgCard, color: T.textSecondary, cursor: 'default' }}>{value || '—'}</div>
    </div>
  );
}

// ── SVG Charts ──
function LineChart({ data, width = 400, height = 120 }: { data: { date: string; value: number }[]; width?: number; height?: number }) {
  if (!data.length) return <div style={{ color: T.textMuted, fontSize: 12, padding: 20, textAlign: 'center' }}>Sem dados</div>;
  const max = Math.max(...data.map(d => d.value), 1);
  const pts = data.map((d, i) => `${(i / (data.length - 1 || 1)) * (width - 20) + 10},${height - 10 - ((d.value / max) * (height - 20))}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline fill="none" stroke="#6C8EF2" strokeWidth="2" points={pts} />
      {data.map((d, i) => {
        const x = (i / (data.length - 1 || 1)) * (width - 20) + 10;
        const y = height - 10 - ((d.value / max) * (height - 20));
        return <circle key={i} cx={x} cy={y} r={2.5} fill="#6C8EF2" />;
      })}
    </svg>
  );
}

function BarChart({ data, width = 300, height = 120 }: { data: { label: string; value: number }[]; width?: number; height?: number }) {
  if (!data.length) return <div style={{ color: T.textMuted, fontSize: 12, padding: 20, textAlign: 'center' }}>Sem dados</div>;
  const max = Math.max(...data.map(d => d.value), 1);
  const barW = Math.min(40, (width - 20) / data.length - 4);
  return (
    <svg width={width} height={height + 20} viewBox={`0 0 ${width} ${height + 20}`}>
      {data.map((d, i) => {
        const barH = (d.value / max) * (height - 10);
        const x = 10 + i * (barW + 4);
        return (
          <g key={i}>
            <rect x={x} y={height - barH} width={barW} height={barH} rx={3} fill="#6C8EF2" opacity={0.8} />
            <text x={x + barW / 2} y={height + 14} textAnchor="middle" fill={T.textSecondary} fontSize={8}>{d.label.slice(0, 6)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Tab definitions ──
const TABS = [
 { id: 'profile', icon: '', label: 'Perfil' },
 { id: 'subscription', icon: '', label: 'Assinatura' },
 { id: 'llm', icon: '', label: 'Limites LLM' },
 { id: 'wallet', icon: '', label: 'Carteira' },
 { id: 'workspaces', icon: '', label: 'Ambientes' },
 { id: 'activity', icon: '', label: 'Atividade' },
 { id: 'security', icon: '', label: 'Seguranca' },
 { id: 'referral', icon: '', label: 'Indicacoes' },
 { id: 'analytics', icon: '', label: 'Analiticos' },
 { id: 'danger', icon: '', label: 'Zona de Perigo' },
];

// ── Notifications Section (used in LLM tab) ──
function NotificationsSection({ userId }: { userId: string }) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get<{ notifications: any[]; unread_count: number }>(`/admin/users/${userId}/notifications?limit=10`);
      setNotifications(res.notifications);
      setUnreadCount(res.unread_count);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.patch(`/admin/users/${userId}/notifications/read-all`, {});
      fetchNotifications();
    } catch (e) { /* ignore */ }
  };

  const typeColors: Record<string, string> = { warning: T.warning, critical: T.danger, info: T.primary, limit_warning: T.warning };
  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m atras`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h atras`;
    return `${Math.floor(hrs / 24)}d atras`;
  };

  return (
    <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.purple, letterSpacing: '0.02em' }}>Notificacoes Recentes</span>
          {unreadCount > 0 && (
            <span style={{ background: T.danger, color: T.text, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10 }}>{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost" size="xs" onClick={markAllRead}>Marcar todas como lidas</Button>
        )}
      </div>
      {loading ? <ListSkeleton rows={3} /> :
        notifications.length === 0 ? <div style={{ color: T.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>Sem notificacoes</div> :
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notifications.map((n: any) => (
            <div key={n.id} style={{ ...S.glass, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, opacity: n.read ? 0.6 : 1 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: typeColors[n.type] || T.primary, marginTop: 5, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.border }}>{n.title}</div>
                <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{n.message}</div>
              </div>
              <div style={{ fontSize: 10, color: T.textMuted, flexShrink: 0, whiteSpace: 'nowrap' }}>{timeAgo(n.created_at)}</div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

export function AdminUserDetail({ id }: { id?: string; path?: string }) {
  if (!isLoggedIn.value) return <LoginPage />;

  const [user, setUser] = useState<UserDetail | null>(null);
  const [wallet, setWallet] = useState<any>(null);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Editable form state
  const [form, setForm] = useState<Record<string, any>>({});

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get<{ user: UserDetail; wallet: any; workspaces: any[]; subscription: Subscription | null; runners: any[]; agents: any[]; router_tokens: any[]; recent_activity: any[] }>(`/admin/users/${id}`);
      setUser(res.user);
      setWallet(res.wallet);
      setWorkspaces(res.workspaces || []);
      setSubscription(res.subscription || null);
      setForm({ ...res.user });
    } catch (e) {
      showToast('Falha ao carregar usuario');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const updateField = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const saveProfile = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${id}`, form);
      showToast('Perfil salvo!');
      fetchUser();
    } catch (e) { showToast('Falha ao salvar'); }
    finally { setSaving(false); }
  };

  const saveLLMLimits = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${id}/llm-limits`, {
        token_limit_monthly: form.token_limit_monthly ?? 0,
        budget_limit_monthly: form.budget_limit_monthly ?? 0,
        budget_limit_daily: form.budget_limit_daily ?? 0,
        rpm_limit: form.rpm_limit ?? 120,
        default_model: form.default_model || null,
        allowed_models: form.allowed_models || null,
        rph_limit: form.rph_limit ?? 0,
        rpd_limit: form.rpd_limit ?? 0,
        spend_limit_per_minute: form.spend_limit_per_minute ?? 0,
        spend_limit_per_hour: form.spend_limit_per_hour ?? 0,
        alert_threshold_pct: form.alert_threshold_pct ?? 80,
        alert_enabled: form.alert_enabled ?? 1,
        alert_email: form.alert_email || null,
      });
      showToast('Limites LLM salvos!');
      fetchUser();
    } catch (e) { showToast('Falha ao salvar'); }
    finally { setSaving(false); }
  };

  const changeSubscriptionPlan = async (plan: string) => {
    if (!id) return;
    try {
      const res = await api.patch<{ subscription: Subscription }>(`/admin/users/${id}/subscription`, { plan });
      setSubscription(res.subscription);
      showToast(`Plano alterado para ${plan.toUpperCase()}`);
    } catch (e) { showToast('Falha ao mudar plano'); }
  };

  const walletAction = async (action: 'credit' | 'debit') => {
    const amountStr = prompt(`Informe o valor para ${action === 'credit' ? 'creditar' : 'debitar'} (em centavos):`);
    if (!amountStr) return;
    const amount = parseInt(amountStr);
    if (isNaN(amount) || amount <= 0) { showToast('Valor invalido'); return; }
    const desc = prompt('Descricao:') || `Admin ${action}`;
    try {
      await api.post(`/admin/users/${id}/wallet/${action}`, { amount, description: desc });
      showToast(`${action === 'credit' ? 'Credito' : 'Debito'} realizado!`);
      fetchUser();
    } catch (e: any) { showToast(e.message || 'Falha na acao'); }
  };

  const suspendUser = async () => {
    if (!confirm('Suspender este usuario?')) return;
    const reason = prompt('Motivo:') || 'Acao administrativa';
    try {
      await api.post(`/admin/users/${id}/suspend`, { reason });
      showToast('Usuario suspenso');
      fetchUser();
    } catch (e) { showToast('Falha'); }
  };

  const unsuspendUser = async () => {
    try {
      await api.post(`/admin/users/${id}/unsuspend`, {});
      showToast('Suspensao removida');
      fetchUser();
    } catch (e) { showToast('Falha'); }
  };

  const resetPassword = async () => {
    try {
      const res = await api.post<{ reset_token: string; expires_at: string }>(`/admin/users/${id}/reset-password-link`, {});
      showToast(`Token de reset: ${res.reset_token}`);
    } catch (e) { showToast('Falha'); }
  };

  const disable2FA = async () => {
    if (!confirm('Desabilitar 2FA deste usuario? Esta e uma acao de emergencia.')) return;
    try {
      await api.post(`/admin/users/${id}/2fa/disable`, {});
      showToast('2FA desabilitado');
      fetchUser();
    } catch (e) { showToast('Falha'); }
  };

  const deleteUser = async () => {
    if (!confirm('DELETAR este usuario? Esta acao NAO pode ser desfeita!')) return;
    if (!confirm('Tem ABSOLUTA certeza?')) return;
    showToast('Exclusao nao implementada (seguranca)');
  };

  if (loading) {
    return (
      <div style={S.page}>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.3; } }`}</style>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Skeleton width={300} height={30} /><div style={{ height: 20 }} />
          {Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ marginBottom: 16 }}><Skeleton height={40} /></div>)}
        </div>
      </div>
    );
  }

  if (!user) return <div style={S.page}><p>Usuario nao encontrado</p></div>;

  const currentPlan = subscription?.plan || 'free';
  const planColor = PLAN_COLORS[currentPlan] || T.textMuted;

  // ── Tab Content Renderers ──
  const renderProfile = () => (
    <div>
      {/* Informacoes Pessoais */}
      <div style={S.sectionTitle}>Informacoes Pessoais</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Nome"><Input value={form.name || ''} onChange={(v) => updateField('name', v)} /></Field>
        <Field label="Email"><Input value={form.email || ''} onChange={(v) => updateField('email', v)} /></Field>
        <Field label="Telefone"><Input value={form.phone || ''} onChange={(v) => updateField('phone', v)} /></Field>
        <Field label="Data de Nascimento"><Input value={form.birth_date || ''} onChange={(v) => updateField('birth_date', v)} /></Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Avatar URL"><Input value={form.avatar_url || ''} onChange={(v) => updateField('avatar_url', v)} /></Field>
        </div>
      </div>

      {/* Endereco */}
      <div style={S.sectionTitle}>Endereco</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Pais"><Input value={form.country || ''} onChange={(v) => updateField('country', v)} /></Field>
        <Field label="Cidade"><Input value={form.city || ''} onChange={(v) => updateField('city', v)} /></Field>
        <Field label="Endereco"><Input value={form.address || ''} onChange={(v) => updateField('address', v)} /></Field>
        <Field label="CEP"><Input value={form.postal_code || ''} onChange={(v) => updateField('postal_code', v)} /></Field>
      </div>

      {/* Configuracoes */}
      <div style={S.sectionTitle}>Configuracoes</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <Field label="Fuso Horario"><Input value={form.timezone || ''} onChange={(v) => updateField('timezone', v)} /></Field>
        <Field label="Idioma"><Input value={form.language || ''} onChange={(v) => updateField('language', v)} /></Field>
        <Field label="Empresa"><Input value={form.company || ''} onChange={(v) => updateField('company', v)} /></Field>
      </div>

      {/* Faturamento */}
      <div style={S.sectionTitle}>Faturamento</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Nome de Faturamento"><Input value={form.billing_name || ''} onChange={(v) => updateField('billing_name', v)} /></Field>
        <Field label="Email de Faturamento"><Input value={form.billing_email || ''} onChange={(v) => updateField('billing_email', v)} /></Field>
        <Field label="Pais de Faturamento"><Input value={form.billing_country || ''} onChange={(v) => updateField('billing_country', v)} /></Field>
        <Field label="CPF/CNPJ"><Input value={form.billing_document || ''} onChange={(v) => updateField('billing_document', v)} /></Field>
      </div>

      {/* Metadados (read-only) */}
      <div style={S.sectionTitle}>Metadados</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <ReadOnlyField label="Criado em" value={user.created_at ? new Date(user.created_at).toLocaleString('pt-BR') : null} />
        <ReadOnlyField label="Atualizado em" value={user.updated_at ? new Date(user.updated_at).toLocaleString('pt-BR') : null} />
        <ReadOnlyField label="Ultima Atividade" value={user.last_activity_at ? new Date(user.last_activity_at).toLocaleString('pt-BR') : null} />
        <ReadOnlyField label="IP de Cadastro" value={user.signup_ip} />
        <ReadOnlyField label="Pais de Cadastro" value={user.signup_country} />
        <ReadOnlyField label="Total de Logins" value={String(user.login_count ?? 0)} />
      </div>

      {/* Notas */}
      <div style={S.sectionTitle}>Notas</div>
      <Field label="Notas internas">
        <Textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} value={form.notes || ''} onInput={(e) => updateField('notes', (e.target as HTMLTextAreaElement).value)} />
      </Field>

      {/* Role */}
      <div style={S.sectionTitle}>Funcao</div>
      <Field label="Funcao do usuario">
        <Select value={form.role || 'CUSTOMER'} onChange={(v) => updateField('role', v)} options={[{ value: 'CUSTOMER', label: 'CUSTOMER' }, { value: 'ADMIN', label: 'ADMIN' }]} />
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <Button variant="primary" onClick={saveProfile} disabled={saving} loading={saving}>{saving ? 'Salvando...' : 'Salvar Perfil'}</Button>
      </div>
    </div>
  );

  const renderSubscription = () => {
    const [changingPlan, setChangingPlan] = useState(false);
    return (
      <div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ ...S.glass, padding: 20, flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Plano Atual</div>
            <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>
              <Badge text={currentPlan.toUpperCase()} color={planColor} />
            </div>
          </div>
          <div style={{ ...S.glass, padding: 20, flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Status</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
              <Badge text={subscription?.status || 'Sem assinatura'} color={STATUS_COLORS[subscription?.status || ''] || T.textMuted} />
            </div>
          </div>
        </div>

        {subscription && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div style={{ ...S.glass, padding: 16 }}>
              <div style={{ fontSize: 11, color: T.textSecondary }}>Periodo</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {subscription.current_period_start ? new Date(subscription.current_period_start).toLocaleDateString('pt-BR') : '—'}
                {' → '}
                {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('pt-BR') : '—'}
              </div>
            </div>
            <div style={{ ...S.glass, padding: 16 }}>
              <div style={{ fontSize: 11, color: T.textSecondary }}>Ciclo de Cobranca</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{subscription.billing_cycle || '—'}</div>
            </div>
            <div style={{ ...S.glass, padding: 16 }}>
              <div style={{ fontSize: 11, color: T.textSecondary }}>Workspaces</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{subscription.workspaces_used ?? 0} / {subscription.workspaces_limit ?? '∞'}</div>
              {(subscription.workspaces_limit ?? 0) > 0 && <ProgressBar value={subscription.workspaces_used ?? 0} max={subscription.workspaces_limit!} />}
            </div>
            <div style={{ ...S.glass, padding: 16 }}>
              <div style={{ fontSize: 11, color: T.textSecondary }}>Creditos LLM</div>
            </div>
            {subscription.stripe_subscription_id && (
              <div style={{ ...S.glass, padding: 16, gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, color: T.textSecondary }}>Stripe Subscription ID</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <code style={{ fontSize: 12, color: T.textSecondary }}>{subscription.stripe_subscription_id}</code>
                  <a href={`https://dashboard.stripe.com/test/subscriptions/${subscription.stripe_subscription_id}`} target="_blank" style={{ ...S.btn, ...S.btnPrimary, textDecoration: 'none', padding: '4px 8px', fontSize: 11 }}>Abrir Stripe</a>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.purple, marginBottom: 12 }}>Mudar Plano</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['free', 'pro', 'business', 'enterprise'].map(plan => (
              <Button
                key={plan}
                disabled={plan === currentPlan}
                onClick={() => changeSubscriptionPlan(plan)}
                variant="ghost"
                size="sm"
                style={{
                  background: plan === currentPlan ? `${PLAN_COLORS[plan]}30` : T.border,
                  color: plan === currentPlan ? PLAN_COLORS[plan] : T.textMuted,
                  border: plan === currentPlan ? `1px solid ${PLAN_COLORS[plan]}50` : `1px solid ${T.bgActive}`,
                  opacity: plan === currentPlan ? 0.6 : 1,
                }}
              >{plan.toUpperCase()}</Button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderLLM = () => {
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Tokens Mensais</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{(user.tokens_used_monthly ?? 0).toLocaleString()} / {(user.token_limit_monthly ?? 0) === 0 ? '∞' : user.token_limit_monthly.toLocaleString()}</div>
            {(user.token_limit_monthly ?? 0) > 0 && <ProgressBar value={user.tokens_used_monthly ?? 0} max={user.token_limit_monthly} />}
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Orcamento Mensal</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>${((user.budget_used_monthly ?? 0) / 100).toFixed(2)} / {(user.budget_limit_monthly ?? 0) === 0 ? '∞' : `$${(user.budget_limit_monthly / 100).toFixed(2)}`}</div>
            {(user.budget_limit_monthly ?? 0) > 0 && <ProgressBar value={user.budget_used_monthly ?? 0} max={user.budget_limit_monthly} color={T.warning} />}
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Orcamento Diario</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>${((user.budget_used_daily ?? 0) / 100).toFixed(2)} / {(user.budget_limit_daily ?? 0) === 0 ? '∞' : `$${(user.budget_limit_daily / 100).toFixed(2)}`}</div>
            {(user.budget_limit_daily ?? 0) > 0 && <ProgressBar value={user.budget_used_daily ?? 0} max={user.budget_limit_daily} color={T.success} />}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Limite de Tokens Mensal (0 = ilimitado)">
            <Input type="number" value={String(form.token_limit_monthly ?? 0)} onChange={(v) => updateField('token_limit_monthly', parseInt(v) || 0)} />
          </Field>
          <Field label="Limite Orcamento Mensal (centavos, 0 = ilimitado)">
            <Input type="number" value={String(form.budget_limit_monthly ?? 0)} onChange={(v) => updateField('budget_limit_monthly', parseInt(v) || 0)} />
          </Field>
          <Field label="Modelo Padrao">
            <Input value={form.default_model || ''} onChange={(v) => updateField('default_model', v)} />
          </Field>
          <Field label="Modelos Permitidos (separados por virgula)">
            <Input value={form.allowed_models || ''} onChange={(v) => updateField('allowed_models', v)} />
          </Field>
        </div>
        {/* Limites Avancados */}
        <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.purple, marginBottom: 16, letterSpacing: '0.02em' }}>Limites Avancados de Taxa</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Field label="RPM (Requisicoes/Min, 0 = ilimitado)">
              <Input type="number" value={String(form.rpm_limit ?? 120)} onChange={(v) => updateField('rpm_limit', parseInt(v) || 0)} />
            </Field>
            <Field label="RPH (Requisicoes/Hora, 0 = ilimitado)">
              <Input type="number" value={String(form.rph_limit ?? 0)} onChange={(v) => updateField('rph_limit', parseInt(v) || 0)} />
            </Field>
            <Field label="RPD (Requisicoes/Dia, 0 = ilimitado)">
              <Input type="number" value={String(form.rpd_limit ?? 0)} onChange={(v) => updateField('rpd_limit', parseInt(v) || 0)} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Field label="Limite Gasto/Min ($, 0 = ilimitado)">
              <input type="number" step="0.01" style={S.input} value={form.spend_limit_per_minute ?? 0} onInput={(e) => updateField('spend_limit_per_minute', parseFloat((e.target as HTMLInputElement).value) || 0)} />
            </Field>
            <Field label="Limite Gasto/Hora ($, 0 = ilimitado)">
              <input type="number" step="0.01" style={S.input} value={form.spend_limit_per_hour ?? 0} onInput={(e) => updateField('spend_limit_per_hour', parseFloat((e.target as HTMLInputElement).value) || 0)} />
            </Field>
            <Field label="Limite Orcamento Diario (centavos, 0 = ilimitado)">
              <Input type="number" value={String(form.budget_limit_daily ?? 0)} onChange={(v) => updateField('budget_limit_daily', parseInt(v) || 0)} />
            </Field>
          </div>
        </div>

        {/* Alertas */}
        <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.purple, marginBottom: 16, letterSpacing: '0.02em' }}>Configuracoes de Alerta</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Field label="Limite de Alerta (%)">
              <input type="number" min="1" max="100" style={S.input} value={form.alert_threshold_pct ?? 80} onInput={(e) => updateField('alert_threshold_pct', parseInt((e.target as HTMLInputElement).value) || 80)} />
            </Field>
            <Field label="Email de Alerta (vazio = email do usuario)">
              <Input placeholder="Usa email do usuario por padrao" value={form.alert_email || ''} onChange={(v) => updateField('alert_email', v)} />
            </Field>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div
              onClick={() => updateField('alert_enabled', form.alert_enabled ? 0 : 1)}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
                background: form.alert_enabled ? T.purple : T.bgActive,
                position: 'relative',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
                left: form.alert_enabled ? 23 : 3, transition: 'left 0.2s',
              }} />
            </div>
            <span style={{ fontSize: 13, color: T.border }}>Alertas Habilitados</span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="primary" onClick={saveLLMLimits} disabled={saving} loading={saving}>{saving ? 'Salvando...' : 'Salvar Limites LLM'}</Button>
        </div>

        {/* Notificacoes */}
        <NotificationsSection userId={id!} />
      </div>
    );
  };

  const renderWallet = () => (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <div style={{ ...S.glass, padding: 20, flex: 1 }}>
          <div style={{ fontSize: 11, color: T.textSecondary }}>Saldo Atual</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: T.success }}>${((wallet?.balance ?? 0) / 100).toFixed(2)}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
          <Button variant="primary" onClick={() => walletAction('credit')}>Adicionar Creditos</Button>
          <Button variant="secondary" onClick={() => walletAction('debit')} style={{ background: T.warning, color: T.text }}>Debitar</Button>
        </div>
      </div>
      <p style={{ color: T.textMuted, fontSize: 12 }}>Historico de transacoes em breve.</p>
    </div>
  );

  const renderWorkspaces = () => (
    <div>
      <div style={{ marginBottom: 16, fontSize: 13, color: T.textSecondary }}>
        {workspaces.length} ambientes • Maximo permitido: {user.max_machines ?? 10}
      </div>
      {workspaces.length === 0 ? <p style={{ color: T.textMuted }}>Nenhum ambiente</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={S.th}>Nome</th><th style={S.th}>Status</th><th style={S.th}>Plano</th>
            <th style={S.th}>Imagem</th><th style={S.th}>Agente</th><th style={S.th}>Criado em</th>
          </tr></thead>
          <tbody>{workspaces.map((w: any) => (
            <tr key={w.id}>
              <td style={S.td}>{w.name || w.id?.slice(0, 8) || '—'}</td>
              <td style={S.td}><Badge text={w.status || '—'} color={STATUS_COLORS[w.status] || T.textMuted} /></td>
              <td style={S.td}>{w.plan || '—'}</td>
              <td style={S.td}><span style={{ fontSize: 11, color: T.textSecondary }}>{w.image || '—'}</span></td>
              <td style={S.td}>{w.agent_name ? <span style={{ fontSize: 11 }}>{w.agent_name} ({w.agent_model || '—'})</span> : <span style={{ color: T.textMuted }}>—</span>}</td>
              <td style={S.td}>{w.created_at ? new Date(w.created_at).toLocaleDateString('pt-BR') : '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );

  const ActivityTab = () => {
    const [activities, setActivities] = useState<any[]>([]);
    const [actPage, setActPage] = useState(1);
    const [actTotal, setActTotal] = useState(0);
    const [actLoading, setActLoading] = useState(true);

    useEffect(() => {
      (async () => {
        setActLoading(true);
        try {
          const res = await api.get<{ activities: any[]; pagination: { total: number } }>(`/admin/users/${id}/activity?page=${actPage}&limit=50`);
          setActivities(res.activities);
          setActTotal(res.pagination.total);
        } catch (e) { }
        setActLoading(false);
      })();
    }, [actPage]);

    if (actLoading) return <div>{Array.from({ length: 5 }).map((_, i) => <div key={i} style={{ marginBottom: 8 }}><Skeleton /></div>)}</div>;
    return (
      <div>
        <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12 }}>{actTotal} eventos no total</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={S.th}>Hora</th><th style={S.th}>Tipo</th><th style={S.th}>Pagina/Alvo</th><th style={S.th}>Detalhe</th><th style={S.th}>IP</th>
          </tr></thead>
          <tbody>{activities.map(a => (
            <tr key={a.id}>
              <td style={S.td}>{new Date(a.created_at).toLocaleString('pt-BR')}</td>
              <td style={S.td}><Badge text={a.event_type} color={a.event_type === 'error' ? T.danger : T.primary} /></td>
              <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.page_url || a.action_target || '—'}</td>
              <td style={{ ...S.td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.action_detail || '—'}</td>
              <td style={S.td}><code style={{ fontSize: 10 }}>{a.ip_address || '—'}</code></td>
            </tr>
          ))}</tbody>
        </table>
        {actTotal > 50 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
            <Button variant="secondary" size="sm" disabled={actPage <= 1} onClick={() => setActPage(p => p - 1)}>← Anterior</Button>
            <span style={{ fontSize: 12, color: T.textSecondary, alignSelf: 'center' }}>Pagina {actPage}</span>
            <Button variant="secondary" size="sm" onClick={() => setActPage(p => p + 1)}>Proxima →</Button>
          </div>
        )}
      </div>
    );
  };

  const renderSecurity = () => (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ ...S.glass, padding: 16 }}>
          <div style={{ fontSize: 11, color: T.textSecondary }}>Ultimo Login</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{user.last_login_at ? new Date(user.last_login_at).toLocaleString('pt-BR') : 'Nunca'}</div>
          <div style={{ fontSize: 11, color: T.textMuted }}>IP: {user.last_login_ip || '—'}</div>
        </div>
        <div style={{ ...S.glass, padding: 16 }}>
          <div style={{ fontSize: 11, color: T.textSecondary }}>Total de Logins</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{user.login_count ?? 0}</div>
        </div>
        <div style={{ ...S.glass, padding: 16 }}>
          <div style={{ fontSize: 11, color: T.textSecondary }}>Tentativas Falhas</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: (user.failed_login_attempts ?? 0) > 5 ? T.danger : T.text }}>{user.failed_login_attempts ?? 0}</div>
        </div>
        <div style={{ ...S.glass, padding: 16 }}>
          <div style={{ fontSize: 11, color: T.textSecondary }}>2FA</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{user.two_factor_enabled ? 'Habilitado' : 'Desabilitado'}</div>
          {user.two_factor_enabled ? <Button variant="danger" size="sm" onClick={disable2FA} style={{ marginTop: 8 }}>Desabilitar 2FA</Button> : null}
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <Button variant="primary" onClick={resetPassword}>Gerar Link de Reset de Senha</Button>
      </div>
    </div>
  );

  const ReferralTab = () => {
    const [referrals, setReferrals] = useState<any>(null);
    const [refLoading, setRefLoading] = useState(true);

    useEffect(() => {
      (async () => {
        try {
          const res = await api.get<any>(`/admin/users/${id}/referrals`);
          setReferrals(res);
        } catch (e) { }
        setRefLoading(false);
      })();
    }, []);

    if (refLoading) return <div>{Array.from({ length: 3 }).map((_, i) => <div key={i} style={{ marginBottom: 8 }}><Skeleton /></div>)}</div>;
    return (
      <div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <div style={{ ...S.glass, padding: 16, flex: 1 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Codigo de Indicacao</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}>{referrals?.referral_code || '—'}</div>
            {referrals?.referral_code && <Button variant="secondary" size="xs" onClick={() => { navigator.clipboard.writeText(referrals.referral_code); showToast('Copiado!'); }} style={{ marginTop: 8 }}>Copiar</Button>}
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Total de Indicacoes</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{referrals?.total ?? 0}</div>
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.textSecondary }}>Indicacoes Premium</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: T.warning }}>{referrals?.total_premium ?? 0}</div>
          </div>
        </div>
        {(referrals?.referrals?.length ?? 0) > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={S.th}>Nome</th><th style={S.th}>Email</th><th style={S.th}>Criado em</th><th style={S.th}>Premium</th></tr></thead>
            <tbody>{referrals.referrals.map((r: any) => (
              <tr key={r.id}><td style={S.td}>{r.name || '—'}</td><td style={S.td}>{r.email}</td><td style={S.td}>{new Date(r.created_at).toLocaleDateString('pt-BR')}</td><td style={S.td}>{r.is_premium ? 'Sim' : '—'}</td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    );
  };

  const AnalyticsTab = () => {
    const [analytics, setAnalytics] = useState<any>(null);
    const [anaLoading, setAnaLoading] = useState(true);

    useEffect(() => {
      (async () => {
        try {
          const res = await api.get<any>(`/admin/users/${id}/analytics`);
          setAnalytics(res);
        } catch (e) { }
        setAnaLoading(false);
      })();
    }, []);

    if (anaLoading) return <div>{Array.from({ length: 4 }).map((_, i) => <div key={i} style={{ marginBottom: 8 }}><Skeleton height={120} /></div>)}</div>;

    const dailyData = (analytics?.daily_usage || []).reverse();
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Tokens (30 dias)</div>
            <LineChart data={dailyData.map((d: any) => ({ date: d.date, value: d.tokens_used ?? 0 }))} />
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Orcamento Gasto (30 dias)</div>
            <BarChart data={dailyData.slice(-14).map((d: any) => ({ label: d.date?.slice(5) || '', value: d.budget_spent ?? 0 }))} />
          </div>
          <div style={{ ...S.glass, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Resumo</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div><span style={{ color: T.textSecondary }}>Tokens/mes:</span> {(analytics?.summary?.tokens_used_monthly ?? 0).toLocaleString()}</div>
              <div><span style={{ color: T.textSecondary }}>Orcamento/mes:</span> ${((analytics?.summary?.budget_used_monthly ?? 0) / 100).toFixed(2)}</div>
              <div><span style={{ color: T.textSecondary }}>Logins:</span> {analytics?.summary?.login_count ?? 0}</div>
              <div><span style={{ color: T.textSecondary }}>Ambientes:</span> {analytics?.summary?.workspaces_count ?? 0}</div>
              <div><span style={{ color: T.textSecondary }}>Total Creditado:</span> ${((analytics?.summary?.total_credited ?? 0) / 100).toFixed(2)}</div>
              <div><span style={{ color: T.textSecondary }}>Total Debitado:</span> ${((analytics?.summary?.total_debited ?? 0) / 100).toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDanger = () => (
    <div>
      <div style={{ ...S.glass, padding: 20, marginBottom: 16, borderColor: T.dangerBg }}>
        <h3 style={{ fontSize: 16, color: T.danger, margin: '0 0 12px' }}>Zona de Perigo</h3>
        <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>Estas acoes sao irreversiveis. Prossiga com cautela.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {user.is_suspended ? (
            <Button variant="success" onClick={unsuspendUser}>Remover Suspensao</Button>
          ) : (
            <Button variant="danger" onClick={suspendUser}>Suspender Conta</Button>
          )}
          <Button variant="danger" onClick={deleteUser}>Deletar Conta</Button>
        </div>
      </div>
    </div>
  );

  const tabContent: Record<string, () => any> = {
    profile: renderProfile,
    subscription: renderSubscription,
    llm: renderLLM,
    wallet: renderWallet,
    workspaces: renderWorkspaces,
    activity: () => <ActivityTab />,
    security: renderSecurity,
    referral: () => <ReferralTab />,
    analytics: () => <AnalyticsTab />,
    danger: renderDanger,
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.3; } }`}</style>

      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: T.bgSecondary, color: T.text, padding: '10px 20px',
          borderRadius: 10, fontSize: 13, border: '1px solid rgba(108,142,242,0.3)',
          boxShadow: `0 8px 24px ${T.blackAlpha40}`,
        }}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <Button variant="secondary" onClick={() => route('/admin/users')}>← Voltar</Button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{user.name || user.email}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <Badge text={user.role} color={user.role === 'ADMIN' ? T.warning : T.textMuted} />
            <Badge text={user.is_suspended ? 'Suspenso' : 'Ativo'} color={user.is_suspended ? T.danger : T.success} />
            <Badge text={currentPlan.toUpperCase()} color={planColor} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, paddingBottom: 8 }}>
        {TABS.map(t => (
          <Button
            key={t.id} onClick={() => setTab(t.id)}
            variant={tab === t.id ? 'secondary' : 'ghost'}
            size="sm"
            style={{
              background: tab === t.id ? 'rgba(108,142,242,0.15)' : 'transparent',
              color: tab === t.id ? T.primary : T.textSecondary,
              border: tab === t.id ? '1px solid rgba(108,142,242,0.3)' : '1px solid transparent',
            }}
          >{t.icon} {t.label}</Button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ ...S.glass, padding: 24 }}>
        {tabContent[tab]?.() || <p>Tab nao encontrada</p>}
      </div>
    </div>
  );
}
